import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import i18n from 'config/i18n'
import { Namespace, useTranslation, UseTranslationOptions, Trans, TransProps } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectTeamList, selectIsInternalUser, TeamState } from 'state/profile'
import { isJSONParsableObject } from 'components/utils/validation'

// handle string & JSON string on get/set localstorage
export function useLocalStorage<T = string>(key: string, initialValue: T): [T, (s: T) => void] {
    const [value, setValue] = useState<T>(() => {
        try {
            const item = localStorage.getItem(key)
            if (item && isJSONParsableObject(item)) {
                const result = JSON.parse(item)
                if (result) return result
            }
            return item ? item : initialValue
        } catch (error) {
            return initialValue
        }
    })

    const setLocalStorageValue = (val: T): void => {
        try {
            setValue(val)
            if (typeof val !== 'string') {
                localStorage.setItem(key, JSON.stringify(val))
            } else {
                localStorage.setItem(key, val)
            }
        } catch (error) {}
    }

    return [value, setLocalStorageValue]
}

export function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>()
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState<number>(() => window.innerWidth)
    useEffect(() => {
        const handleResize = () => {
            setWindowSize(window.innerWidth)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])
    return windowSize
}

export function useIsMobileView() {
    const windowSize = useWindowSize()

    return windowSize <= 768
}
export function useWindowHeight() {
    const [windowHeight, setWindowHeight] = useState<number | undefined>(undefined)
    const handleResize = () => {
        setWindowHeight(window.innerHeight)
    }
    useEffect(() => {
        window.addEventListener('resize', handleResize)
        handleResize()
        return () => window.removeEventListener('resize', handleResize)
    }, [])
    return windowHeight
}

export function useIsLoginRoute() {
    const [isLoginRoute, setIsLoginRoute] = useState(false)
    const notShowPath = ['/reset-password', '/login', '/forgot-password', '/sign-up', '/register']
    const location = useLocation()

    useEffect(() => {
        const isLoginRoute = notShowPath.includes(location.pathname)
        setIsLoginRoute(isLoginRoute)
    }, [location])

    return isLoginRoute
}

export function useNewTranslation(ns?: Namespace, options?: UseTranslationOptions) {
    return useTranslation(ns, { ...options, i18n: i18n })
}

export function NewTrans(props: TransProps): any {
    return Trans({ ...props, i18n: i18n })
}

/**
 * Hook to do history push with the existing params
 *
 */
export function useHistoryPushWithForwardedParams() {
    const history = useHistory()
    const location = useLocation()

    const pushWithForwardedParams = (
        newPath: string,
        overrides: Record<string, string> = {},
        options: { replace?: boolean } = {},
    ) => {
        const [basePath, newQueryString] = newPath.split('?')
        const newPathParams = new URLSearchParams(newQueryString || '')

        const currentParams = new URLSearchParams(location.search)

        currentParams.forEach((value, key) => {
            if (!newPathParams.has(key)) {
                newPathParams.set(key, value)
            }
        })

        Object.entries(overrides).forEach(([key, value]) => {
            newPathParams.set(key, value)
        })

        const params = {
            pathname: basePath,
            search: newPathParams.toString(),
        }

        if (options.replace) {
            history.replace(params)
        } else {
            history.push(params)
        }
    }

    return pushWithForwardedParams
}

/**
 * Hook to check the deleteTeam next redirect path
 *
 * @param teamUuid
 * @returns the redirect path
 */
export const useDeleteTeamRedirectPath = (teamUuid: string | undefined): string => {
    const teamList = useSelector(selectTeamList)
    const isInternalUser = useSelector(selectIsInternalUser)
    const redirectPath = useMemo(() => {
        const newTeamList = teamList.filter((team: TeamState) => team.id !== teamUuid)
        if (isInternalUser) {
            return '/teams'
        } else if (!isInternalUser && newTeamList.length === 0) {
            return '/login'
        } else {
            return `/team/${newTeamList[0].id}/members`
        }
    }, [teamList, isInternalUser])
    return teamUuid ? redirectPath : '/submissions'
}

export const useDebounce = <T>(callback: (args: T) => void, delay: number) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])
    const debouncedCallback = (args: T) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            callback(args)
        }, delay)
    }

    return debouncedCallback
}

export const useRedirect = () => {
    const history = useHistory()
    const redirect = useCallback((path: string) => {
        history.push(path)
    }, [])
    const redirectToLogin = () => redirect('/login')
    return { redirectToLogin, redirect }
}

export { useIsRoleManagementValuesChanged } from './teams'
export { useLocationTranslation } from './translation'
export { useDisplayTooltip } from './displayTooltip'
export { useGetCommentCardTitle } from './comment'
